import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/page-header';
import { StatCard } from '../components/stat-card';
import { RecentKudosCard } from '../components/kudos';
import { UpcomingBirthdaysCard } from '../components/birthdays';
import { UpcomingAnniversariesCard } from '../components/anniversaries';
import { useInvoiceStatistics } from '../hooks';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  DollarSign,
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: statistics } = useInvoiceStatistics();

  useEffect(() => {
    // Redirect to onboarding if user doesn't have birth date
    // Photo is optional, so we only check for birthDate
    if (user && !user.birthDate) {
      navigate('/onboarding');
    }
  }, [user, navigate]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={t('welcome', { name: user?.name })}
        description={t('title')}
        testId="dashboard-header"
      />

      {statistics && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6" data-testid="dashboard-stats-grid">
          <StatCard
            title={t('stats.totalInvoices')}
            value={statistics.total}
            icon={<FileText className="h-5 w-5" />}
            variant="primary"
            testId="dashboard-stat-total"
            onClick={() => navigate('/invoices')}
          />
          <StatCard
            title={t('stats.pending')}
            value={statistics.pending}
            icon={<Clock className="h-5 w-5" />}
            variant="warning"
            testId="dashboard-stat-pending"
            onClick={() => navigate('/invoices?status=pending')}
          />
          <StatCard
            title={t('stats.underReview')}
            value={statistics.underReview}
            icon={<Eye className="h-5 w-5" />}
            variant="default"
            testId="dashboard-stat-under-review"
            onClick={() => navigate('/invoices?status=under_review')}
          />
          <StatCard
            title={t('stats.approved')}
            value={statistics.approved}
            icon={<CheckCircle className="h-5 w-5" />}
            variant="success"
            testId="dashboard-stat-approved"
            onClick={() => navigate('/invoices?status=approved')}
          />
          <StatCard
            title={t('stats.rejected')}
            value={statistics.rejected}
            icon={<XCircle className="h-5 w-5" />}
            variant="destructive"
            testId="dashboard-stat-rejected"
            onClick={() => navigate('/invoices?status=rejected')}
          />
          <StatCard
            title={t('stats.paid')}
            value={statistics.paid}
            icon={<DollarSign className="h-5 w-5" />}
            variant="success"
            testId="dashboard-stat-paid"
            onClick={() => navigate('/invoices?status=paid')}
          />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div data-testid="dashboard-upcoming-birthdays">
            <UpcomingBirthdaysCard days={30} />
          </div>
          <div data-testid="dashboard-upcoming-anniversaries">
            <UpcomingAnniversariesCard days={30} />
          </div>
        </div>
        <div data-testid="dashboard-recent-kudos">
          <RecentKudosCard limit={10} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
